import { getValue, parseDate, recordMatchesFilters } from "./calculations";
import { getMappingLabel, SYNTHETIC_FIELDS } from "./defaults";
import {
  ClassifiedRecord,
  ClassifiedRecords,
  MappingKey,
  ReportConfiguration,
  ReportState,
} from "./types";

/**
 * Turning the data-quality percentages into something actionable.
 *
 * The old traceability card said "554/555 creator cost populated" and stopped there, which
 * tells you a number is wrong but not which record to go fix. This splits the same
 * information into named issues, each carrying the records responsible, so the panel can
 * list them and link straight to the task.
 *
 * Records deliberately excluded by period or status are not data-quality problems and are
 * omitted from this report. Only missing data is surfaced and counted against the score.
 */

export interface RecordIssue {
  key: string;
  label: string;
  /** Source fields that prove the issue or identify who should resolve it. */
  evidenceMappings: MappingKey[];
  /** What this does to the numbers on screen. */
  impact: string;
  records: ClassifiedRecord[];
}

export interface IssueReport {
  /** Records contributing to the totals. */
  contributingCount: number;
  /** Records contributing to the totals, retained for the traceability table. */
  contributingRecords: ClassifiedRecord[];
  /** Contributing records that do not have a data-quality defect. */
  goodContributingCount: number;
  /** Distinct records named by at least one data-quality issue. */
  attentionCount: number;
  /** goodContributingCount / (goodContributingCount + attentionCount). */
  score: number;
  defects: RecordIssue[];
}

/** Identifying details for one problem record, plus a link to go fix it. */
export interface ProblemRecord {
  key: string;
  campaign: string;
  creator: string;
  createdDate: string;
  createdTimestamp: number;
  date: string;
  status: string;
  evidence: Partial<Record<MappingKey, string | number | null | undefined>>;
  /** Deep link to the task, when the row carries the ids needed to build one. */
  href: string | null;
}

const has = (
  row: ClassifiedRecord,
  configuration: ReportConfiguration,
  key: Parameters<typeof getValue>[2],
): boolean => Boolean(getValue(row.record, configuration, key));

export function buildIssueReport(
  classified: ClassifiedRecords,
  state: ReportState,
): IssueReport {
  const { configuration } = state;
  const contributing = classified.eligible;

  // Missing allocation only affects completeness when the row carries money that should
  // contribute. Rows with neither revenue nor cost have no financial impact.
  const unallocated = classified.excluded.filter(
    (row) =>
      row.date === null &&
      (row.rawRevenue !== null || row.rawCost !== null) &&
      (configuration.statusMode === "all" ||
        configuration.includedStatuses.includes(row.status)) &&
      recordMatchesFilters(row.record, row.date, state),
  );

  const allDefects: RecordIssue[] = [
    {
      key: "missingDate",
      label: "No allocation month",
      evidenceMappings: ["date", "budgetMonth", "revenue", "cost", "manager"],
      impact:
        "Neither Reporting date nor Budget month is set, so revenue or cost is excluded from every period.",
      records: unallocated,
    },
    {
      key: "missingRevenue",
      label: "Revenue not set",
      evidenceMappings: ["revenue"],
      impact: "Counted as zero, so revenue and margin are understated.",
      records: contributing.filter((row) => row.rawRevenue === null),
    },
    {
      key: "missingCost",
      label: "Creator cost not set",
      evidenceMappings: ["cost"],
      impact: "Counted as zero, so profit and margin are overstated.",
      records: contributing.filter((row) => row.rawCost === null),
    },
    {
      key: "missingCreator",
      label: "No creator identity",
      evidenceMappings: ["creator"],
      impact: "Not counted in the creator total.",
      records: contributing.filter(
        (row) => !has(row, configuration, "creator"),
      ),
    },
    {
      key: "missingClient",
      label: "No client",
      evidenceMappings: ["client"],
      impact: 'Grouped under "Unassigned" in a client breakdown.',
      records: contributing.filter((row) => !has(row, configuration, "client")),
    },
    {
      key: "missingManager",
      label: `No ${getMappingLabel(configuration.roleLabels, "manager").toLowerCase()}`,
      evidenceMappings: ["manager"],
      impact: `Grouped under "Unassigned" in a ${getMappingLabel(configuration.roleLabels, "manager").toLowerCase()} breakdown.`,
      records: contributing.filter(
        (row) => !has(row, configuration, "manager"),
      ),
    },
  ];
  const defects = allDefects.filter((issue) => issue.records.length > 0);

  // A record is clean when no defect names it.
  //
  // Unallocated financial records count even though they are not contributing. Scoring
  // only the contributing rows would report 100% for a report that silently dropped most
  // of its source data - which is precisely the failure this panel exists to surface.
  const flagged = new Set<ClassifiedRecord>();
  defects.forEach((issue) => issue.records.forEach((row) => flagged.add(row)));

  const attentionCount = flagged.size;
  const goodContributingCount = contributing.filter(
    (row) => !flagged.has(row),
  ).length;
  const qualityRecordCount = goodContributingCount + attentionCount;

  return {
    contributingCount: contributing.length,
    contributingRecords: contributing,
    goodContributingCount,
    attentionCount,
    score: qualityRecordCount ? goodContributingCount / qualityRecordCount : 1,
    defects,
  };
}

/**
 * Flattens records into displayable rows. `workspaceUuid` is only used to build the task
 * link; without the campaign and task ids on the row the link is omitted rather than
 * guessed at.
 */
export function toProblemRecords(
  records: ClassifiedRecord[],
  configuration: ReportConfiguration,
  evidenceMappings: MappingKey[],
  workspaceUuid: string,
): ProblemRecord[] {
  return records
    .map((row, index) => {
      const projectUuid = row.record[SYNTHETIC_FIELDS.projectUuid];
      const taskUuid = row.record[SYNTHETIC_FIELDS.taskUuid];
      const created = row.record[SYNTHETIC_FIELDS.taskCreatedDate];
      const createdTimestamp = Date.parse(String(created ?? ""));

      return {
        key: String(taskUuid ?? index),
        campaign: String(
          row.record[SYNTHETIC_FIELDS.projectName] ||
            getValue(row.record, configuration, "campaignName") ||
            "-",
        ),
        creator: String(
          row.record[SYNTHETIC_FIELDS.taskName] ||
            getValue(row.record, configuration, "creatorName") ||
            "-",
        ),
        createdDate: Number.isNaN(createdTimestamp)
          ? "-"
          : new Date(createdTimestamp).toLocaleString("en-GB"),
        createdTimestamp: Number.isNaN(createdTimestamp) ? 0 : createdTimestamp,
        date: row.date ? row.date.toISOString().slice(0, 10) : "no date",
        status: row.status || "-",
        evidence: Object.fromEntries(
          evidenceMappings.map((mapping) => [
            mapping,
            getValue(row.record, configuration, mapping),
          ]),
        ),
        href:
          projectUuid && taskUuid
            ? `/workspace/${workspaceUuid}/projects/${projectUuid}/${taskUuid}`
            : null,
      };
    })
    .sort((a, b) => b.createdTimestamp - a.createdTimestamp);
}

/** Parse a raw date the same way the classifier does, for display of undated rows. */
export const displayDate = (value: unknown): string => {
  const parsed = parseDate(value);
  return parsed ? parsed.toISOString().slice(0, 10) : "no date";
};
